import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArrowLeft, Pencil, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STATUS_BADGE } from "@/lib/constants";
import { formatINR, formatSqft, mediaUrl, yesNo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Item = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-widest text-slate-400">{label}</dt>
    <dd className="mt-0.5 text-slate-800">{value == null || value === "" ? "—" : value}</dd>
  </div>
);

const Section = ({ title, children }) => (
  <section className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8">
    <h2 className="font-heading text-lg font-semibold text-emerald-950 mb-5 pb-3 border-b border-slate-100">{title}</h2>
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">{children}</dl>
  </section>
);

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";

  const { data: p, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => (await api.get(`/properties/${id}`)).data,
  });

  const archiveMutation = useMutation({
    mutationFn: async () => (await api.post(`/properties/${id}/archive`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property", id] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      toast.success("Property archived");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  if (isLoading || !p) {
    return <div className="p-8 text-slate-400" data-testid="property-detail-loading">Loading…</div>;
  }

  return (
    <div className="p-6 sm:p-8 max-w-5xl" data-testid="property-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate("/properties")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-900 mb-3 transition-colors" data-testid="back-to-properties">
            <ArrowLeft className="h-4 w-4" /> All Properties
          </button>
          <h1 className="font-heading text-3xl font-bold text-emerald-950 tracking-tight" data-testid="property-title">{p.title}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Badge variant="outline" className={STATUS_BADGE[p.status]} data-testid="property-status-badge">{p.status}</Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-600">{p.property_type}{p.property_subtype ? ` · ${p.property_subtype}` : ""}</Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-600">For {p.listing_type}</Badge>
            <span className="font-heading text-xl font-semibold text-emerald-900 ml-2">
              {formatINR(p.price_or_rent_amount)}{p.listing_type === "Rent" ? "/mo" : ""}
            </span>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/properties/${id}/edit`)} data-testid="edit-property-button" className="rounded-lg">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            {p.status !== "Archived" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="archive-property-button" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Archive className="h-4 w-4 mr-2" /> Archive
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive this property?</AlertDialogTitle>
                    <AlertDialogDescription>
                      It will be marked "Archived" and hidden from active listings. Nothing is deleted — historical deals stay intact.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => archiveMutation.mutate()} data-testid="confirm-archive-button" className="rounded-lg bg-red-600 hover:bg-red-700">
                      Archive
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      <div className="space-y-6 animate-fade-up">
        {(p.photo_urls.length > 0 || p.video_urls.length > 0) && (
          <section className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8" data-testid="property-media-section">
            <h2 className="font-heading text-lg font-semibold text-emerald-950 mb-5 pb-3 border-b border-slate-100">Photos &amp; Videos</h2>
            {p.photo_urls.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                {p.photo_urls.map((u) => (
                  <a key={u} href={mediaUrl(u)} target="_blank" rel="noopener noreferrer">
                    <img src={mediaUrl(u)} alt="Property" className="h-36 w-full object-cover rounded-lg border border-slate-200 hover:opacity-90 transition-opacity" />
                  </a>
                ))}
              </div>
            )}
            {p.video_urls.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {p.video_urls.map((u) => (
                  <video key={u} src={mediaUrl(u)} controls className="w-full rounded-lg border border-slate-200 bg-black" />
                ))}
              </div>
            )}
          </section>
        )}

        <Section title="Property Info">
          <Item label="Property Type" value={p.property_type} />
          <Item label="Subtype" value={p.property_subtype} />
          <Item label="Listing Type" value={p.listing_type} />
          <Item label="Condition" value={p.condition_status} />
          <Item label="Entry Date" value={p.entry_date} />
          <Item label="Status" value={p.status} />
        </Section>

        <Section title="Owner Info">
          <Item label="Owner Name" value={p.owner_name} />
          <Item label="Phone (Primary)" value={<a href={`tel:${p.owner_phone_primary}`} className="text-emerald-800 font-medium inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{p.owner_phone_primary}</a>} />
          <Item label="Phone (Secondary)" value={p.owner_phone_secondary} />
          <Item label="Email" value={p.owner_email} />
          <Item label="Date of Birth" value={p.owner_dob} />
          <Item label="Occupation" value={p.owner_occupation} />
          <Item label="Address" value={p.owner_address} />
        </Section>

        <Section title="Location">
          <Item label="Survey No." value={p.survey_no} />
          <Item label="Address Line" value={p.address_line} />
          <Item label="Locality" value={p.locality} />
          <Item label="City" value={p.city} />
          <Item label="Unit / Flat No." value={p.unit_flat_no} />
          <Item label="Floor" value={p.floor_number} />
          <Item label="Total Floors" value={p.building_total_floors} />
        </Section>

        <Section title="Dimensions">
          <Item label="Saleable Area" value={p.saleable_area_sqft && formatSqft(p.saleable_area_sqft)} />
          <Item label="Plot Area" value={p.plot_area_sqft && formatSqft(p.plot_area_sqft)} />
          <Item label="Carpet Area" value={p.carpet_area_sqft && formatSqft(p.carpet_area_sqft)} />
        </Section>

        <Section title="Price">
          <Item label={p.listing_type === "Rent" ? "Rent / month" : "Price"} value={formatINR(p.price_or_rent_amount)} />
          <Item label="Per sqft" value={p.price_or_rent_per_sqft && formatINR(p.price_or_rent_per_sqft)} />
          <Item label="Deposit" value={p.deposit_amount && formatINR(p.deposit_amount)} />
          <Item label="Maintenance / month" value={p.maintenance_charge_monthly && formatINR(p.maintenance_charge_monthly)} />
          <Item label="Rent Escalation" value={p.rent_escalation_percent && `${p.rent_escalation_percent}%`} />
          <Item label="Price Note" value={p.price_note} />
        </Section>

        <Section title="Features">
          <Item label="BHK" value={p.bhk_count} />
          <Item label="Bedrooms" value={p.bedrooms} />
          <Item label="Bathrooms" value={p.bathrooms} />
          <Item label="Furnishing" value={p.furnishing_status} />
          <Item label="Age (years)" value={p.property_age_years} />
          <Item label="Balconies" value={p.total_balconies} />
          <Item label="Lift" value={yesNo(p.lift_available)} />
          <Item label="Ceiling Height (ft)" value={p.height_ft} />
          <Item label="Frontage (ft)" value={p.frontage_ft} />
          <Item label="Power Supply" value={p.power_supply} />
          <Item label="Water Supply" value={p.water_supply} />
        </Section>

        <Section title="Additional Info">
          <Item label="Parking" value={p.parking_details} />
          <Item label="Keys With" value={p.keys_with} />
          <Item label="Facing" value={p.facing_direction} />
          <Item label="From Bus Stand" value={p.distance_from_bus_stand} />
          <Item label="From Railway Station" value={p.distance_from_railway_station} />
          <Item label="From Market" value={p.distance_from_market} />
          <Item label="Amenities" value={p.amenities.length ? p.amenities.join(", ") : null} />
          <Item label="Reference Source" value={p.reference_source} />
          <Item label="Declaration Signed" value={yesNo(p.declaration_signed)} />
          <Item label="Comments" value={p.comments} />
        </Section>
      </div>
    </div>
  );
}
